/**
 * SAMBA configuration API schemas
 */

import { z } from 'zod';
import { InternalRole } from '@pillar-portal/database/dist/frontend';
import { PositiveIntSchema } from './common.schemas';
import {
    AllowedWebsiteSchemaIPG,
    AllowedWebsiteSchemaIPW,
    AllowedWebsiteSchemaUnmanaged,
    type MerchantConfig,
    type RoleAssignmentCustomConfig,
    MerchantConfigSchema,
    MerchantConfigStorageWriteSchema,
    RoleAssignmentCustomConfigSchema,
    UserRoleAssignmentConfigPatchSchema,
} from '../backend_config.types';
import type {
    EffectiveConfigResponse,
    NavigateMenuWebsitesResponse,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest,
    RoleAssignmentConfigResponse,
    SambaAutoupdateConfig,
    SambaInternalRole,
    UpdateConfigRequest,
    UpdateRoleAssignmentConfigRequest,
} from '../samba-config.types';
import { NonEmptyStringSchema } from './common.schemas';

/**
 * Config response (single config object)
 */
export const ConfigResponseSchema: z.ZodType<MerchantConfig> = MerchantConfigSchema;

export const RoleAssignmentUserConfigResponseSchema: z.ZodType<RoleAssignmentCustomConfig> =
    RoleAssignmentCustomConfigSchema;

export const SambaInstallerVersionSchema: z.ZodType<string> = NonEmptyStringSchema;

export const SambaAutoupdateConfigSchema: z.ZodType<SambaAutoupdateConfig> = z
    .object({
        sambaInstallerVersion: SambaInstallerVersionSchema,
        forceAutoupdate: z.boolean(),
    })
    .strict();

export const SambaInternalRoleSchema: z.ZodType<SambaInternalRole> =
    z.enum(InternalRole);

export const EffectiveConfigResponseExtensionSchema: z.ZodType<{
    sambaAutoupdate: SambaAutoupdateConfig;
    internalRole?: SambaInternalRole;
}> = z.object({
    sambaAutoupdate: SambaAutoupdateConfigSchema,
    internalRole: SambaInternalRoleSchema.optional(),
});

export const EffectiveConfigResponseSchema: z.ZodType<EffectiveConfigResponse> =
    z.intersection(MerchantConfigSchema, EffectiveConfigResponseExtensionSchema);

export const NavigateMenuWebsiteSchema: z.ZodType<
    NavigateMenuWebsitesResponse['websites'][number]
> = z.union([
    AllowedWebsiteSchemaIPG,
    AllowedWebsiteSchemaIPW,
    AllowedWebsiteSchemaUnmanaged,
]);

export const NavigateMenuWebsitesResponseSchema:
    z.ZodType<NavigateMenuWebsitesResponse> = z.object({
    websites: z.array(NavigateMenuWebsiteSchema),
});

/**
 * Update config request
 */
export const UpdateConfigRequestSchema: z.ZodType<UpdateConfigRequest> = z.object({
    config: MerchantConfigStorageWriteSchema,
});

export const UpdateRoleAssignmentConfigRequestSchema: z.ZodType<UpdateRoleAssignmentConfigRequest> =
    z.object({
        config: RoleAssignmentCustomConfigSchema,
    });

export const PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema:
    z.ZodType<PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest> = z.object({
    userRoleAssignmentConfig: UserRoleAssignmentConfigPatchSchema,
});

/**
 * Update config response
 */
export const UpdateConfigResponseSchema: z.ZodType<MerchantConfig> = MerchantConfigSchema;

/**
 * Combined config response
 */
export const CombinedConfigResponseSchema = z.object({
    user_config: RoleAssignmentUserConfigResponseSchema,
    org_config: ConfigResponseSchema,
});

/**
 * Role assignment config response
 */
export const RoleAssignmentConfigResponseSchema: z.ZodType<RoleAssignmentConfigResponse> = z.object({
    role_assignment_id: z.number(),
    user_config: RoleAssignmentUserConfigResponseSchema,
});

/**
 * Get effective config response
 */
export const GetEffectiveConfigResponseSchema = z.object({
    config: MerchantConfigSchema,
    version: z.string(),
});

/**
 * Common param schemas
 */
export const AuditIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const SessionUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentUuidParamSchema: z.ZodType<string> = z.string().uuid();
